import {
  getAccount,
  readContract,
  writeContract,
  waitForTransactionReceipt,
  simulateContract,
} from "@wagmi/core";
import { BaseError, ContractFunctionRevertedError, formatUnits } from "viem";
import { wagmiConfig } from "@/context/WalletProvider";
import { ESCROW_ABI, ERC20_ABI } from "@/lib/onchainAbis";
import { NATIVE_TOKEN } from "@/lib/tokens";

const short = (addr) => (addr ? `${addr.slice(0, 6)}…${addr.slice(-4)}` : "?");

function friendlyDecodedError(reverted) {
  const name = reverted?.data?.errorName;
  const args = reverted?.data?.args ?? [];
  if (name === "ERC20InsufficientBalance") {
    return "Your wallet doesn't hold enough of the payment token for this amount. Top up your balance and try again.";
  }
  if (name === "ERC20InsufficientAllowance") {
    return "The escrow's spending approval is too low. Retry — you'll be asked to approve again.";
  }
  if (name === "Error" && typeof args[0] === "string") return args[0]; // require(...) strings
  return null;
}

function revertReason(err) {
  if (err instanceof BaseError) {
    const reverted = err.walk((e) => e instanceof ContractFunctionRevertedError);
    if (reverted instanceof ContractFunctionRevertedError) {
      return (
        reverted.reason ||
        friendlyDecodedError(reverted) ||
        reverted.data?.args?.[0] ||
        null // undecodable custom error — never surface the raw selector
      );
    }
    if (/user (rejected|denied)/i.test(err.shortMessage ?? "")) return "Transaction was rejected in the wallet.";
    return null;
  }
  return err?.message;
}

async function simulateThenWrite(params, action = "complete this transaction") {
  const account = getAccount(wagmiConfig).address;
  try {
    const { request } = await simulateContract(wagmiConfig, { account, ...params });
    const hash = await writeContract(wagmiConfig, request);
    await waitForTransactionReceipt(wagmiConfig, { hash });
    return hash;
  } catch (err) {
    // Technical detail stays in the console; users never see raw selectors.
    console.error(`[onchain] ${params.functionName} failed:`, err);
    throw new Error(revertReason(err) || `We couldn't ${action}. Please try again or contact support.`);
  }
}

/**
 * The escrow only accepts certain wallets as the tx submitter — a refund
 * settle may ONLY come from the wallet that funded the deal. Catch mismatches
 * before submitting instead of letting the chain revert.
 */
function assertExpectedWallet(expected, why) {
  const list = (Array.isArray(expected) ? expected : [expected]).filter(Boolean);
  if (!list.length) return;
  const connected = getAccount(wagmiConfig).address;
  if (!connected) throw new Error("Connect your wallet first.");
  if (!list.some((a) => a.toLowerCase() === connected.toLowerCase())) {
    throw new Error(
      `Wrong wallet connected (${short(connected)}). Switch to ${list.map(short).join(" or ")} — ${why}.`,
    );
  }
}

/** Preflight the token balance so "insufficient funds" is caught before any wallet prompt. */
async function assertTokenBalance(token, owner, amount) {
  try {
    const [balance, decimals] = await Promise.all([
      readContract(wagmiConfig, { address: token, abi: ERC20_ABI, functionName: "balanceOf", args: [owner] }),
      readContract(wagmiConfig, { address: token, abi: ERC20_ABI, functionName: "decimals", args: [] }).catch(() => 18),
    ]);
    if (balance < amount) {
      throw new Error(
        `Insufficient balance: your wallet holds ${formatUnits(balance, decimals)} but this needs ${formatUnits(amount, decimals)}. Top up and try again.`,
      );
    }
  } catch (e) {
    if (e?.message?.startsWith("Insufficient balance")) throw e;
    // balance read failed (RPC hiccup) — let simulation handle it
  }
}

export async function fundMilestoneOnchain(proof, onStep) {
  const escrow = proof.escrowAddress;
  const token = proof.paymentToken;
  const amount = BigInt(proof.amount);
  const isNative = token.toLowerCase() === NATIVE_TOKEN.toLowerCase();

  assertExpectedWallet(proof.payer, "the wallet linked to your account as the payer");

  const fundArgs = [
    proof.signature,
    proof.token,
    BigInt(proof.timeToExpire),
    proof.payer,
    proof.escrowId,
    token,
    amount,
    proof.dealHash,
  ];

  if (isNative) {
    onStep?.({ step: 1, total: 1, kind: "fund" });
    return simulateThenWrite(
      {
        address: escrow,
        abi: ESCROW_ABI,
        functionName: "fundDeal",
        args: fundArgs,
        value: amount,
      },
      "fund this milestone",
    );
  }

  await assertTokenBalance(token, proof.payer, amount);

  const allowance = await readContract(wagmiConfig, {
    address: token,
    abi: ERC20_ABI,
    functionName: "allowance",
    args: [proof.payer, escrow],
  });

  const needsApproval = allowance < amount;
  const total = needsApproval ? 2 : 1;

  if (needsApproval) {
    onStep?.({ step: 1, total, kind: "approve" });
    await simulateThenWrite(
      {
        address: token,
        abi: ERC20_ABI,
        functionName: "approve",
        args: [escrow, amount],
      },
      "approve the payment token",
    );
  }

  onStep?.({ step: total, total, kind: "fund" });
  return simulateThenWrite(
    {
      address: escrow,
      abi: ESCROW_ABI,
      functionName: "fundDeal",
      args: fundArgs,
    },
    "fund this milestone",
  );
}

export async function reviseMilestoneOnchain(proof, onStep) {
  const escrow = proof.escrowAddress;
  const token = proof.paymentToken;
  const amount = BigInt(proof.amount);
  const isNative = token.toLowerCase() === NATIVE_TOKEN.toLowerCase();
  const args = [proof.signature, proof.token, BigInt(proof.timeToExpire), proof.escrowId, amount];

  assertExpectedWallet(proof.payer, "the wallet linked to your account as the payer");
  if (proof.direction === "topup" && !isNative) await assertTokenBalance(token, proof.payer, amount);

  if (proof.direction === "refund") {
    onStep?.({ step: 1, total: 1, kind: "refund" });
    return simulateThenWrite({ address: escrow, abi: ESCROW_ABI, functionName: "refundPartial", args });
  }

  if (isNative) {
    onStep?.({ step: 1, total: 1, kind: "topup" });
    return simulateThenWrite({ address: escrow, abi: ESCROW_ABI, functionName: "addFunds", args, value: amount });
  }

  const allowance = await readContract(wagmiConfig, {
    address: token,
    abi: ERC20_ABI,
    functionName: "allowance",
    args: [proof.payer, escrow],
  });
  const needsApproval = allowance < amount;
  const total = needsApproval ? 2 : 1;
  if (needsApproval) {
    onStep?.({ step: 1, total, kind: "approve" });
    await simulateThenWrite({ address: token, abi: ERC20_ABI, functionName: "approve", args: [escrow, amount] });
  }
  onStep?.({ step: total, total, kind: "topup" });
  return simulateThenWrite({ address: escrow, abi: ESCROW_ABI, functionName: "addFunds", args });
}

export async function settleMilestoneOnchain(proof) {
  assertExpectedWallet(
    proof.allowedSubmitters?.length ? proof.allowedSubmitters : [proof.payer, proof.payee],
    "only the funding wallet (or the payout recipient) can submit this settlement",
  );
  return simulateThenWrite(
    {
      address: proof.escrowAddress,
      abi: ESCROW_ABI,
      functionName: "settleDeal",
      args: [
        proof.signature,
        proof.token,
        BigInt(proof.timeToExpire),
        proof.escrowId,
        proof.payee,
        BigInt(proof.toPayee),
        BigInt(proof.toPayer),
      ],
    },
    "claim this payment",
  );
}
